import { fetchAuthSession } from 'aws-amplify/auth';

const BASE_URL = ((import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api').replace(/\/$/, '');

function buildUrl(endpoint: string): string {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${BASE_URL}${cleanEndpoint}`;
}

async function getAuthToken(): Promise<string | null> {
  // Cognito-authenticated users: get token from Amplify
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) return idToken;
  } catch {
    // Not a Cognito session — fall through
  }
  // Join-code-authenticated users (under-16 high school): token stored in localStorage
  return localStorage.getItem('studywize_access_token');
}

export async function apiFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);

  const token = await getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const method = (options.method ?? 'GET').toUpperCase();
  // Don't force JSON Content-Type for FormData — browser sets multipart boundary automatically
  if ((method === 'POST' || method === 'PUT') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(buildUrl(endpoint), {
    ...options,
    headers,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body: unknown = await response.json();
      if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
        detail = body.error;
      }
    } catch {
      // Response body wasn't JSON — fall back to statusText
    }
    throw new Error(`API error ${response.status}: ${detail}`);
  }

  return response.json() as Promise<T>;
}
